import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Loader2, Upload, X, FileText, Video as VideoIcon, ImageIcon } from 'lucide-react';
import { resolveMediaUrl, API } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Reusable media uploader for images/videos/documents.
 * - Persists value as URL string (or array for `multiple`)
 * - Uses backend object-storage endpoints
 *
 * Props:
 *   value: string | string[]
 *   onChange: (newValue) => void
 *   kind: 'image' | 'video' | 'document'
 *   multiple?: boolean
 *   label?: string
 *   maxSizeMB?: number
 */
export const MediaUpload = ({
  value,
  onChange,
  kind = 'image',
  multiple = false,
  label,
  maxSizeMB = 50,
  testId,
}) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const accept = kind === 'image' ? 'image/*' : kind === 'video' ? 'video/*' : '.pdf,.doc,.docx';
  const endpoint = kind === 'image' ? '/upload/image' : kind === 'video' ? '/upload/video' : '/upload/document';

  const handleSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Login required to upload');
      return;
    }

    setUploading(true);
    const newUrls = [];
    try {
      for (const file of files) {
        if (file.size > maxSizeMB * 1024 * 1024) {
          toast.error(`${file.name}: exceeds ${maxSizeMB}MB`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}${endpoint}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        newUrls.push(data.url);
      }
      if (multiple) {
        const current = Array.isArray(value) ? value : [];
        onChange([...current, ...newUrls]);
      } else if (newUrls.length) {
        onChange(newUrls[0]);
      }
      toast.success('Uploaded');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (idx) => {
    if (multiple && Array.isArray(value)) {
      onChange(value.filter((_, i) => i !== idx));
    } else {
      onChange('');
    }
  };

  const items = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

  const renderPreview = (item, idx) => {
    const url = resolveMediaUrl(item);
    if (kind === 'video') {
      return (
        <div key={idx} className="relative group w-32 h-24 rounded-lg overflow-hidden bg-gray-900 border">
          <video src={url} className="w-full h-full object-cover" muted />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <VideoIcon className="h-6 w-6 text-white" />
          </div>
          <button
            type="button"
            onClick={() => removeAt(idx)}
            className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-1"
            data-testid={`media-remove-${idx}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      );
    }
    if (kind === 'document') {
      return (
        <div key={idx} className="relative flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
          <FileText className="h-5 w-5 text-brand-rust" />
          <a href={url} target="_blank" rel="noreferrer" className="text-sm text-brand-rust hover:underline truncate max-w-[200px]">
            {(item || '').split('/').pop()}
          </a>
          <button type="button" onClick={() => removeAt(idx)} className="text-gray-500 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      );
    }
    return (
      <div key={idx} className="relative group w-32 h-24 rounded-lg overflow-hidden border">
        <img src={url} alt="" className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={() => removeAt(idx)}
          className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-1"
          data-testid={`media-remove-${idx}`}
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <p className="text-sm font-medium text-gray-700">{label}</p>}
      <div className="flex flex-wrap items-start gap-3">
        {items.map((item, idx) => renderPreview(item, idx))}
        {(multiple || !items.length) && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-32 h-24 border-2 border-dashed border-gray-300 hover:border-brand-orange rounded-lg flex flex-col items-center justify-center text-gray-500 hover:text-brand-orange transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                {kind === 'image' && <ImageIcon className="h-5 w-5 mb-1" />}
                {kind === 'video' && <VideoIcon className="h-5 w-5 mb-1" />}
                {kind === 'document' && <FileText className="h-5 w-5 mb-1" />}
                <span className="text-xs">Upload</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleSelect}
      />
    </div>
  );
};
